import { useState } from "react";
import { Phone, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion";

const inputCls = "w-full border border-girder bg-steel px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-hivis focus:outline-none";
const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-slate-500";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", hp: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent", { description: "We reply within one business day, 8am-5pm MST." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader kicker="Contact" title="Talk to a human in Colorado" sub="Questions on sizing, wholesale, or a franchise rollout — call us or send the form." image="/assets/img/w-contact-header-0869.webp" />
      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-5 lg:px-12 lg:py-24">
        <Reveal className="lg:col-span-2">
          <div className="space-y-6" data-testid="contact-info">
            {[
              { icon: Phone, label: "Phone", value: "970-650-5173", href: "tel:9706505173" },
              { icon: MapPin, label: "Location", value: "Montrose, CO 81401" },
              { icon: Clock, label: "Hours", value: "8am - 5pm MST" },
            ].map((c) => (
              <div key={c.label} className="flex items-start gap-4 border-b border-girder pb-6">
                <c.icon className="mt-1 h-5 w-5 text-hivis" />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{c.label}</p>
                  {c.href ? <a href={c.href} className="mt-1 block text-slate-100 transition-colors hover:text-hivis">{c.value}</a> : <p className="mt-1 text-slate-100">{c.value}</p>}
                </div>
              </div>
            ))}
            <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Outdoor Display Products LLC</p>
          </div>
        </Reveal>
        <Reveal i={1} className="lg:col-span-3">
          <form onSubmit={submit} className="clip-corner border border-girder bg-steel/40 p-6 sm:p-8" data-testid="contact-form">
            {/* Honeypot: invisible to humans; bots that fill it are silently dropped server-side */}
            <input type="text" name="hp" value={form.hp} onChange={set("hp")} tabIndex={-1} autoComplete="off" aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} data-testid="hp-field" />
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className={labelCls} htmlFor="ct-name">Name *</label><input id="ct-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="ct-name" /></div>
              <div><label className={labelCls} htmlFor="ct-email">Email *</label><input id="ct-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="ct-email" /></div>
              <div><label className={labelCls} htmlFor="ct-phone">Phone</label><input id="ct-phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="ct-phone" /></div>
              <div><label className={labelCls} htmlFor="ct-subject">Subject</label><input id="ct-subject" value={form.subject} onChange={set("subject")} className={inputCls} data-testid="ct-subject" placeholder="Sizing question, wholesale, rollout..." /></div>
            </div>
            <div className="mt-4"><label className={labelCls} htmlFor="ct-message">Message *</label><textarea id="ct-message" required rows={5} value={form.message} onChange={set("message")} className={inputCls} data-testid="ct-message" /></div>
            <button type="submit" disabled={busy} data-testid="contact-submit" className="clip-corner mt-6 flex items-center justify-center gap-3 bg-hivis px-8 py-3.5 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-hivis-hover disabled:opacity-60">
              {busy ? "Sending..." : "Send Message"} <Send className="h-4 w-4" />
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
